import { Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import StatefulArtworkGrid from 'src/components/ArtworkList/StatefulArtworkGrid'
import {
  useRecommendedArtworkFeed,
  useSimilarArtworkFeed,
} from 'src/components/ArtworkList/useArtworkFeed'
import { OrderOption } from 'src/services/helpers/orderOptionParams'
import { useOrpcSimilarArtworks } from 'src/services/orpc/similarArtwork'
import {
  getArtworkLink,
  getArtworkTitle,
  shouldArtworkBeBlurred,
} from 'src/utils/artwork'
import { cx } from 'src/utils/cx'
import { useBreakpoint } from 'src/utils/hooks/useBreakpoint'
import { track } from 'src/utils/tracker'
import { useNarrowScreenForArtworkDetail } from '../ArtworkDetail/ArtworkDetail/utils'
import ArtworkGrid from '../ArtworkList/ArtworkGrid'
import Empty from '../Empty'
import { CircularSpinner } from '../GenerationTask/Common/CircularSpinner'
import BaseArtworkItemPreviewCard from './BaseArtworkItemPreviewCard'

/**
 * For standalone page
 * @param props
 * @returns
 */
export default function ArtworkRelevantArtworks(props: { artworkId: string }) {
  return (
    <div>
      <Suspense>
        <ArtworkRelevantGrid artworkId={props.artworkId} />
      </Suspense>
    </div>
  )
}

function ArtworkRelevantGrid({ artworkId }: { artworkId: string }) {
  const order = OrderOption.mostLiked
  const feedResult = useSimilarArtworkFeed({ artworkId, first: 24 })
  return (
    <StatefulArtworkGrid
      key={artworkId}
      feedResult={feedResult}
      order={order}
      trackingArgs={{ feed: order, scene: 'artwork-relevant' }}
      pageSize={24}
      renderEmpty={() => <EmptyForRelevantArtworks />}
    />
  )
}

export function ArtworkRelevantArtworksInlineForDetailsSection(props: {
  artworkId: string
}) {
  const { t } = useTranslation(['artwork'])
  const { artworks, loading } = useOrpcSimilarArtworks({
    artworkId: props.artworkId,
    first: 4,
  })

  const narrowScreen = useNarrowScreenForArtworkDetail(false)
  const isLg = useBreakpoint('lg', 'up')
  // Only display 3 cards on < lg
  // Extra 1 are hidden
  const slicedArtworks = artworks.slice(0, isLg ? 4 : 3)
  const shouldHide = !loading && artworks.length < 1
  // Hide whole section if no relevant artworks are available
  if (shouldHide) return null
  return (
    <section>
      <div className="flex items-center mt-4 mb-2">
        <h2 className="font-medium text-base">
          {t('artwork:similar-images.title')}
        </h2>
        <Link
          to={`/artwork/${props.artworkId}/relevant`}
          className="text-theme-primary ml-auto text-sm"
          onClick={() => {
            track('show_similar_image_click', { scene: 'adp-inline-small' })
          }}
        >
          {t('artwork:show.more')}
        </Link>
      </div>
      <div className="w-full">
        {loading && slicedArtworks.length === 0 && <CircularSpinner />}
        <div
          className={cx(
            'w-full relative grid gap-2',
            narrowScreen ? 'grid-cols-3' : 'grid-cols-4',
          )}
        >
          {!loading && slicedArtworks.length > 0
            ? slicedArtworks.map(artwork => {
                const blurForPreview = shouldArtworkBeBlurred(artwork)
                const cardLabel = getArtworkTitle(artwork)
                return (
                  <BaseArtworkItemPreviewCard
                    key={artwork.id}
                    mediaId={artwork.mediaId}
                    media={artwork.media}
                    nsfwBlur={blurForPreview}
                    showExplicitIcon={blurForPreview}
                    artwork={artwork}
                    label={
                      <span className="truncate" title={cardLabel}>
                        {cardLabel}
                      </span>
                    }
                    contentClassName="no-underline"
                    border="plain"
                    hideShare
                    showInfo={false}
                    to={getArtworkLink(artwork)}
                  />
                )
              })
            : null}
        </div>
      </div>
    </section>
  )
}

export function ArtworkRelevantArtworksInline(props: { artworkId: string }) {
  const { t } = useTranslation(['artwork'])
  const { artworks, loading } = useOrpcSimilarArtworks({
    artworkId: props.artworkId,
    first: 4,
  })

  const isLg = useBreakpoint('lg', 'up')
  // Only display 2 cards on < lg
  // Extra 2 are hidden
  const slicedArtworks = artworks.slice(0, isLg ? 4 : 2)

  const shouldHide = !loading && artworks.length < 1

  // Hide whole section if no relevant artworks are available
  if (shouldHide) return null

  return (
    <section>
      <div className="flex items-center mb-4">
        <h2 className="font-medium text-xl">
          {t('artwork:similar-images.title')}
        </h2>
        <Link
          to={`/artwork/${props.artworkId}/relevant`}
          className="text-theme-primary ml-auto"
          onClick={() => {
            track('show_similar_image_click', { scene: 'adp-inline' })
          }}
        >
          {t('artwork:show.more')}
        </Link>
      </div>
      <div className="w-full">
        {loading && slicedArtworks.length === 0 && <CircularSpinner />}
        {!loading && slicedArtworks.length > 0 ? (
          <ArtworkGrid
            artworks={slicedArtworks}
            loading={loading}
            containerProps={{
              className:
                'grid grid-flow-col grid-cols-2 gap-2 pt-2 pb-8 lg:grid-cols-4 lg:gap-3',
            }}
            trackingArgs={{ scene: 'artwork-relevant' }}
          />
        ) : null}
      </div>
    </section>
  )
}

function EmptyForRelevantArtworks() {
  const { t } = useTranslation(['artwork'])

  return <Empty description={t('artwork:relevant-page.empty-placeholder')} />
}

export function ArtworkRecommendedArtworks(props: {
  trackingArgs?: Record<string, any>
}) {
  return (
    <div>
      <Suspense>
        <ArtworkRecommendedGrid trackingArgs={props.trackingArgs} />
      </Suspense>
    </div>
  )
}

function ArtworkRecommendedGrid(props: { trackingArgs?: Record<string, any> }) {
  const order = OrderOption.recommended
  const feedResult = useRecommendedArtworkFeed({ first: 24 })
  return (
    <StatefulArtworkGrid
      feedResult={feedResult}
      showDislike
      enableTracking
      renderEmpty={() => null}
      trackingArgs={{ ...props.trackingArgs, feed: order }}
    />
  )
}
